import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop, PropType } from '@typegoose/typegoose'
import { BaseEntity } from '../base/BaseEntity'
import { Customer } from '../customer/Customer'
import { OrderItem } from './OrderItem'
import { Payment } from '../payment/Payment'
import { Address } from '../address/Address'
import { Shipping } from '../shipping/Shipping'
import { Communication } from '../communication/Communication'
import type { OrderStatusEnum } from './OrderStatusEnum'
import { PaymentBalance } from '../payment/PaymentBalance'
import { DocumentLock } from '../documentLock/DocumentLock'
import { RelatedEntity } from '../base/RelatedEntity'
import type { OrderPaymentStatusEnum } from './OrderPaymentStatusEnum'
import { Activity } from '../activity/Activity'
import { Asset } from '../asset/Asset'
import { User } from '../user/User'
import { WorkOrder } from '../work-order/WorkOrder'
import { generateEntityId } from '../generateEntityId'

@ObjectType()
@InputType('OrderInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class Order extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('so') })
  declare _id: string

  @Field(() => Float)
  @Prop({ type: Number })
  net: number

  @Field(() => String)
  @Prop({ type: String })
  declare status: OrderStatusEnum

  @Field(() => Float)
  @Prop({ type: Number })
  gross: number

  @Field(() => Float)
  @Prop({ type: Number })
  minimumOrderAmount: number

  @Field(() => Float)
  @Prop({ type: Number })
  discount: number

  @Field(() => Float)
  @Prop({ type: Number })
  discountPercent: number

  @Field(() => Float)
  @Prop({ type: Number })
  taxAmount: number

  @Field(() => Float)
  @Prop({ type: Number })
  shippingFee: number

  @Field(() => String)
  @Prop({ type: String })
  type: string

  @Field(() => String)
  @Prop({ type: String })
  customerPoNumber: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  isManualShippingRate: boolean

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  allowPrePayment: boolean

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  isManualTaxAmount: boolean

  @Field(() => String)
  @Prop({ type: String })
  manualTrackingUrl: string

  @Field(() => String)
  @Prop({ type: String })
  manualTrackingNumber: string

  @Field(() => String)
  @Prop({ type: String })
  manualShippingCarrier: string

  @Field(() => Float)
  @Prop({ type: Number })
  initialShippingFee: number

  @Field(() => Float)
  @Prop({ type: Number })
  initialOrderNet: number

  @Field(() => String)
  @Prop({ type: String })
  paymentStatus: OrderPaymentStatusEnum

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  isRemindInCart: boolean

  @Field(() => String)
  @Prop({ type: String })
  paymentTerms: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  isCart: boolean

  @Field(() => String)
  @Prop({ type: String })
  qboId: string

  @Field(() => Float)
  @Prop({ type: Number })
  checkoutStage: number

  @Field(() => Customer)
  @Prop({ type: Customer })
  by: Customer

  @Field(() => [OrderItem])
  @Prop({ type: () => [OrderItem] })
  items: OrderItem[]

  @Field(() => Payment, { deprecationReason: 'Not needed anymore' })
  @Prop({ type: Payment })
  payment: Payment

  @Field(() => [Payment])
  @Prop({ type: [Payment] })
  payments: Payment[]

  @Field(() => Address)
  @Prop({ type: Address })
  shippingAddress: Address

  @Field(() => Address)
  @Prop({ type: Address })
  billingAddress: Address

  @Field(() => Shipping)
  @Prop({ type: Shipping })
  shipping: Shipping

  @Field(() => [User])
  @Prop({ type: [RelatedEntity] })
  members: User[]

  @Field(() => Communication)
  @Prop({ type: Communication })
  communication: Communication

  //This has field resolver, but we put it here for type-safety too
  @Field(() => Float)
  totalWeight: number

  //This has field resolver, but we put it here for type-safety too
  @Field(() => Boolean)
  allItemsInStockByOnHandAndComputedQuantity: boolean

  // Channel-neutral conversation handle (field resolver): the linked conversation's externalId.
  // Lets the communication UI mount the thread without re-reading anything else.
  @Field(() => String, { nullable: true })
  activeConversationId?: string

  //This has field resolver, but we put it here for type-safety too
  @Field(() => PaymentBalance)
  paymentBalance: PaymentBalance

  @Field(() => String)
  @Prop({ type: String })
  invoiceNotes: string

  //This has field resolver, but we put it here for type-safety too
  @Field(() => DocumentLock)
  documentLock: DocumentLock

  /**
   * We store here the last index of the order that was splitted.
   * This is just stored on the parent order, no need to store on child
   * The use this on auto-generation of order number
   */
  @Field(() => Number)
  @Prop({ type: Number })
  lastSplitIndex: number

  /**
   * These are the _id of orders that are related to this order
   */
  @Field(() => [Order])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedOrders: ReturnType<() => Order[]>

  @Field(() => [Asset])
  @Prop({ type: [Asset] })
  assets: Asset[]

  @Field(() => [Activity])
  @Prop({ type: [Activity] })
  remarksActivity: Activity[]

  @Field(() => [WorkOrder])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedWorkOrders: ReturnType<() => WorkOrder[]>
}
